import React, { useState } from 'react';
import styled from 'styled-components';
import { FiDownload, FiUpload, FiExternalLink, FiTrendingUp, FiTrendingDown, FiDollarSign, FiShield, FiClock } from 'react-icons/fi';

const PortfolioContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #3d4de6 0%, #6366f1 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  font-size: 1.125rem;
  color: #a0a0a0;
  max-width: 600px;
  margin: 0 auto;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
`;

const StatCard = styled.div`
  background: rgba(30, 30, 63, 0.6);
  border: 1px solid #2d2d5f;
  border-radius: 16px;
  padding: 1.5rem;
  backdrop-filter: blur(10px);
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const StatTitle = styled.h3`
  color: #a0a0a0;
  font-size: 0.875rem;
  font-weight: 500;
  text-transform: uppercase;
`;

const StatIcon = styled.div`
  color: ${props => props.color || '#3d4de6'};
  font-size: 1.25rem;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 0.5rem;
`;

const StatChange = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.875rem;
  color: ${props => props.positive ? '#22c55e' : '#ef4444'};
`;

const HoldingsSection = styled.section`
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: #ffffff;
`;

const HoldingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const HoldingCard = styled.div`
  background: rgba(30, 30, 63, 0.6);
  border: 1px solid #2d2d5f;
  border-radius: 16px;
  padding: 1.5rem;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: #3d4de6;
    box-shadow: 0 8px 32px rgba(61, 77, 230, 0.2);
  }
`;

const HoldingHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const HoldingInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const HoldingIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
`;

const HoldingDetails = styled.div``;

const HoldingName = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 0.25rem;
`;

const HoldingSymbol = styled.span`
  color: #a0a0a0;
  font-size: 0.875rem;
`;

const HoldingAmount = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 0.5rem;
`;

const HoldingValue = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding: 1rem;
  background: rgba(61, 77, 230, 0.1);
  border-radius: 8px;
`;

const ValueItem = styled.div`
  text-align: center;
`;

const ValueLabel = styled.div`
  font-size: 0.75rem;
  color: #a0a0a0;
  margin-bottom: 0.25rem;
`;

const ValueAmount = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #ffffff;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const ActionButton = styled.button`
  flex: 1;
  padding: 0.75rem 1rem;
  background: ${props => props.primary ? 'linear-gradient(135deg, #3d4de6 0%, #6366f1 100%)' : 'rgba(255, 255, 255, 0.1)'};
  color: white;
  border-radius: 8px;
  text-align: center;
  font-weight: 600;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${props => props.primary ? 'rgba(61, 77, 230, 0.4)' : 'rgba(255, 255, 255, 0.2)'};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  background: rgba(30, 30, 63, 0.6);
  border: 1px solid #2d2d5f;
  border-radius: 16px;
  backdrop-filter: blur(10px);
`;

const EmptyIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
`;

const EmptyTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 0.5rem;
`;

const EmptyText = styled.p`
  color: #a0a0a0;
  margin-bottom: 2rem;
`;

const EmptyButton = styled.button`
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #3d4de6 0%, #6366f1 100%);
  color: white;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(61, 77, 230, 0.4);
  }
`;

const TransactionHistory = styled.section`
  margin-bottom: 3rem;
`;

const TransactionTable = styled.div`
  background: rgba(30, 30, 63, 0.6);
  border: 1px solid #2d2d5f;
  border-radius: 16px;
  backdrop-filter: blur(10px);
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 120px 150px 120px 120px 150px 120px 1fr;
  padding: 1rem 1.5rem;
  background: rgba(61, 77, 230, 0.1);
  border-bottom: 1px solid #2d2d5f;
  font-size: 0.875rem;
  font-weight: 600;
  color: #a0a0a0;
  text-transform: uppercase;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 120px 150px 120px 120px 150px 120px 1fr;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #2d2d5f;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(61, 77, 230, 0.05);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  color: ${props => props.color || '#ffffff'};
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => {
    switch (props.status) {
      case 'completed': return 'rgba(34, 197, 94, 0.2)';
      case 'pending': return 'rgba(249, 115, 22, 0.2)';
      case 'failed': return 'rgba(239, 68, 68, 0.2)';
      default: return 'rgba(156, 163, 175, 0.2)';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'completed': return '#22c55e';
      case 'pending': return '#f97316';
      case 'failed': return '#ef4444';
      default: return '#9ca3af';
    }
  }};
`;

const Portfolio = () => {
  const [holdings] = useState([
    {
      id: 'sugar',
      name: 'Sugar Token',
      symbol: 'SUGAR',
      icon: '🍯',
      amount: 2.5,
      value: 3118.75,
      price: 1247.50,
      change: 2.4,
      positive: true,
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    },
    {
      id: 'xcbg',
      name: 'XCB Gold',
      symbol: 'XCBG',
      icon: '🥇',
      amount: 1.2,
      value: 2441.76,
      price: 2034.80,
      change: 1.8,
      positive: true,
      gradient: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
    }
  ]);

  const [transactions] = useState([
    {
      id: '1',
      type: 'buy',
      commodity: 'SUGAR',
      amount: 1.5,
      price: 1245.20,
      total: 1867.80,
      fee: 2.49,
      status: 'completed',
      date: '2024-01-15 14:32:10',
      hash: 'cb1234...5678'
    },
    {
      id: '2',
      type: 'buy',
      commodity: 'XCBG',
      amount: 1.2,
      price: 2030.45,
      total: 2436.54,
      fee: 4.07,
      status: 'completed',
      date: '2024-01-14 09:15:22',
      hash: 'cb2345...6789'
    },
    {
      id: '3',
      type: 'buy',
      commodity: 'SUGAR',
      amount: 1.0,
      price: 1250.00,
      total: 1250.00,
      fee: 2.50,
      status: 'pending',
      date: '2024-01-15 16:45:33',
      hash: 'cb3456...7890'
    }
  ]);

  const totalValue = holdings.reduce((sum, holding) => sum + holding.value, 0);
  const totalChange = holdings.reduce((sum, holding) => sum + (holding.value * holding.change / 100), 0);

  return (
    <PortfolioContainer>
      <Header>
        <Title>Your Portfolio</Title>
        <Subtitle>
          Track your commodity token holdings, transaction history, and redemption options.
        </Subtitle>
      </Header>

      <StatsGrid>
        <StatCard>
          <StatHeader>
            <StatTitle>Total Portfolio Value</StatTitle>
            <StatIcon color="#22c55e"><FiDollarSign /></StatIcon>
          </StatHeader>
          <StatValue>${totalValue.toLocaleString()}</StatValue>
          <StatChange positive={totalChange >= 0}>
            {totalChange >= 0 ? <FiTrendingUp /> : <FiTrendingDown />}
            {totalChange >= 0 ? '+' : ''}${Math.abs(totalChange).toFixed(2)} today
          </StatChange>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatTitle>Total Holdings</StatTitle>
            <StatIcon color="#3d4de6"><FiShield /></StatIcon>
          </StatHeader>
          <StatValue>{holdings.length}</StatValue>
          <StatChange positive={true}>
            <FiTrendingUp />
            Commodities owned
          </StatChange>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatTitle>Avg. Performance</StatTitle>
            <StatIcon color="#f59e0b"><FiTrendingUp /></StatIcon>
          </StatHeader>
          <StatValue>+2.1%</StatValue>
          <StatChange positive={true}>
            <FiTrendingUp />
            Last 24 hours
          </StatChange>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatTitle>Last Transaction</StatTitle>
            <StatIcon color="#6366f1"><FiClock /></StatIcon>
          </StatHeader>
          <StatValue>Today</StatValue>
          <StatChange positive={true}>
            <FiTrendingUp />
            Buy 1.5 SUGAR
          </StatChange>
        </StatCard>
      </StatsGrid>

      <HoldingsSection>
        <SectionTitle>Current Holdings</SectionTitle>
        
        {holdings.length > 0 ? (
          <HoldingsGrid>
            {holdings.map((holding) => (
              <HoldingCard key={holding.id}>
                <HoldingHeader>
                  <HoldingInfo>
                    <HoldingIcon gradient={holding.gradient}>
                      {holding.icon}
                    </HoldingIcon>
                    <HoldingDetails>
                      <HoldingName>{holding.name}</HoldingName>
                      <HoldingSymbol>{holding.symbol}</HoldingSymbol>
                    </HoldingDetails>
                  </HoldingInfo>
                  <StatChange positive={holding.positive}>
                    {holding.positive ? <FiTrendingUp /> : <FiTrendingDown />}
                    {holding.positive ? '+' : ''}{holding.change}%
                  </StatChange>
                </HoldingHeader>

                <HoldingAmount>{holding.amount} {holding.symbol}</HoldingAmount>

                <HoldingValue>
                  <ValueItem>
                    <ValueLabel>Current Price</ValueLabel>
                    <ValueAmount>${holding.price.toLocaleString()}</ValueAmount>
                  </ValueItem>
                  <ValueItem>
                    <ValueLabel>Total Value</ValueLabel>
                    <ValueAmount>${holding.value.toLocaleString()}</ValueAmount>
                  </ValueItem>
                  <ValueItem>
                    <ValueLabel>24h Change</ValueLabel>
                    <ValueAmount style={{ color: holding.positive ? '#22c55e' : '#ef4444' }}>
                      {holding.positive ? '+' : ''}${((holding.value * holding.change) / 100).toFixed(2)}
                    </ValueAmount>
                  </ValueItem>
                </HoldingValue>

                <ActionButtons>
                  <ActionButton primary>
                    <FiUpload />
                    Sell
                  </ActionButton>
                  <ActionButton>
                    <FiDownload />
                    Redeem
                  </ActionButton>
                  <ActionButton>
                    <FiExternalLink />
                    Details
                  </ActionButton>
                </ActionButtons>
              </HoldingCard>
            ))}
          </HoldingsGrid>
        ) : (
          <EmptyState>
            <EmptyIcon>📈</EmptyIcon>
            <EmptyTitle>No holdings yet</EmptyTitle>
            <EmptyText>Start trading commodity tokens to build your portfolio</EmptyText>
            <EmptyButton>Start Trading</EmptyButton>
          </EmptyState>
        )}
      </HoldingsSection>

      <TransactionHistory>
        <SectionTitle>Transaction History</SectionTitle>
        
        <TransactionTable>
          <TableHeader>
            <div>Type</div>
            <div>Commodity</div>
            <div>Amount</div>
            <div>Price</div>
            <div>Total</div>
            <div>Status</div>
            <div>Date</div>
          </TableHeader>
          
          {transactions.map((tx) => (
            <TableRow key={tx.id}>
              <TableCell color={tx.type === 'buy' ? '#22c55e' : '#ef4444'}>
                {tx.type === 'buy' ? 'BUY' : 'SELL'}
              </TableCell>
              <TableCell>{tx.commodity}</TableCell>
              <TableCell>{tx.amount}</TableCell>
              <TableCell>${tx.price.toLocaleString()}</TableCell>
              <TableCell>${tx.total.toLocaleString()}</TableCell>
              <TableCell>
                <StatusBadge status={tx.status}>
                  {tx.status.toUpperCase()}
                </StatusBadge>
              </TableCell>
              <TableCell>{new Date(tx.date).toLocaleDateString()}</TableCell>
            </TableRow>
          ))}
        </TransactionTable>
      </TransactionHistory>
    </PortfolioContainer>
  );
};

export default Portfolio; 